/**
 * Tilt Race host entry. Multi-mode host SDK, one shared pairing QR,
 * 60Hz physics loop. Phones send their tilt as joyL angle + joyL-
 * power; the host integrates that into marble velocity each tick.
 */

import './style.css';
import '@forgesworn/joystick-pairing-ui/style.css';
import { mountGameControls } from '@forgesworn/joystick-pairing-ui';
import QRCode from 'qrcode';
import listingSource from './listing.json';
import { checkGameListing, controllerSpecFromListing } from '@forgesworn/joystick-protocol';
import { startControllerHost, type ControllerHost, type ControllerSpec } from '@forgesworn/joystick-host';
import {
  tryStartRace, addMarble, applyInput, MAX_PLAYERS, MAX_SPEED, newWorld, PITS,
  PLAYER_COLOURS, removeMarble, step, triggerShake, type World,
} from './game.js';
import {
  acceptName, buildSpectatorView as buildSpectatorViewData, finishLabel, pauseMessage,
  playerSpec, spectatorSpec as spectatorSpecFor,
} from './controller-strings.js';
import { draw, type RenderFrame, type CheerBurst } from './render.js';
import {
  setAudioPaused, initAudio, isMuted, rumble, sfxBoost, sfxFinish, sfxPit, sfxWallThud,
  stopRumble, toggleMuted,
} from './audio.js';

// Controls come from the game's published listing (src/listing.json), so the
// catalogue entry and the controller the phone draws cannot drift apart.
const listingCheck = checkGameListing(listingSource);
if (!listingCheck.ok) throw new Error(`Invalid game listing: ${listingCheck.errors.join('; ')}`);
const LISTING_SPEC = controllerSpecFromListing(listingCheck.listing)!;

const PAD_URL = import.meta.env.VITE_PAD_URL ?? 'https://pad.forgesworn.dev';
const BROKER = import.meta.env.VITE_CONTROLLER_WS ?? 'wss://controller.pallasite.app/';

const canvas = document.getElementById('game');
if (!(canvas instanceof HTMLCanvasElement)) throw new Error('canvas missing');
const ctx = canvas.getContext('2d');
if (!ctx) throw new Error('2d context missing');

const world: World = newWorld();
const joinState = new Map<number, 'open' | 'taken'>();
for (let i = 0; i < MAX_PLAYERS; i++) joinState.set(i, 'open');

let qrDataUrl: string | null = null;
const host: ControllerHost = startControllerHost({
  ws: BROKER,
  multi: true,
  pwaUrl: PAD_URL,
});

let bootHidden = false;
host.onStatus((s) => {
  const bootEl = document.getElementById('boot');
  const reconnectEl = document.getElementById('reconnect');
  if (s.kind === 'waiting' || s.kind === 'paired') {
    if (!bootHidden && bootEl) {
      bootEl.classList.add('hidden');
      setTimeout(() => bootEl.remove(), 600);
      bootHidden = true;
    }
    if (reconnectEl) {
      reconnectEl.classList.remove('show');
      reconnectEl.classList.add('hidden');
    }
  } else if (s.kind === 'reconnecting' || s.kind === 'closed') {
    if (bootHidden && reconnectEl) {
      reconnectEl.classList.add('show');
      reconnectEl.classList.remove('hidden');
    }
  }
});
void QRCode.toDataURL(host.pairingUrl, {
  margin: 2,
  width: 1000,
  color: { dark: '#0a0a18', light: '#cfd8ff' },
}).then((url) => { qrDataUrl = url; });

// Spectator slots — phones past MAX_PLAYERS. They get a CHEER-only
// controller and periodic leaderboard frames.
const spectatorSlots = new Set<number>();
const cheers: CheerBurst[] = [];

function spectatorSpec(slot: number): ControllerSpec {
  return spectatorSpecFor(slot, PLAYER_COLOURS[slot % PLAYER_COLOURS.length] ?? '#b48cff');
}

function buildSpectatorView(): ReturnType<typeof buildSpectatorViewData> {
  return buildSpectatorViewData({
    marbles: [...world.marbles.values()],
    raceKind: world.race.kind,
    names: playerNames,
    tournamentTotals,
    colourFor: (p) => PLAYER_COLOURS[p % PLAYER_COLOURS.length] ?? '#ffffff',
    lastCheerSlot: cheers.length > 0 ? cheers[cheers.length - 1]!.slot : null,
  });
}

const readyPlayer: Parameters<ControllerHost['onPlayerJoin']>[0] = (player) => {
  if (player.p >= MAX_PLAYERS) {
    spectatorSlots.add(player.p);
    host.sendToPlayer(player.p, { type: 'controller-spec', spec: spectatorSpec(player.p) });
    host.sendSpectatorView(player.p, buildSpectatorView());
    return;
  }
  // Kick the bot occupying this slot so the human takes its place
  // with a fresh score. Subsequent autoFillAI calls won't re-add an
  // AI here as long as the human is paired.
  if (!world.marbles.has(player.p) || world.marbles.get(player.p)!.ai) {
    removeMarble(world, player.p);
    addMarble(world, player.p, /* ai */ false);
  }
  joinState.set(player.p, 'taken');
  const colour = PLAYER_COLOURS[player.p % PLAYER_COLOURS.length]!;
  // Tilt is part of the shared controller contract on web and native clients.
  const spec: ControllerSpec = playerSpec(LISTING_SPEC, player.p, colour);
  host.sendToPlayer(player.p, { type: 'controller-spec', spec });
};
host.onPlayerJoin(readyPlayer);
host.onPlayerResume(readyPlayer);
host.onPlayerSuspend((player) => {
  const marble = world.marbles.get(player.p);
  if (marble) {
    marble.inThrust = false;
    marble.inPower = 0;
    marble.boostUntil = world.time;
  }
});

// Tournament context — phones announce their running total + chosen
// name on pair-up.
const tournamentTotals = new Map<number, number>();
const playerNames = new Map<number, string>();
host.onPhoneContext((p, ctx) => {
  if (ctx) {
    tournamentTotals.set(p, ctx.tournamentTotal);
    const name = acceptName(ctx.name);
    if (name) playerNames.set(p, name);
    else playerNames.delete(p);
  } else {
    tournamentTotals.delete(p);
    playerNames.delete(p);
  }
});

host.onPlayerLeave((player) => {
  if (spectatorSlots.delete(player.p)) {
    tournamentTotals.delete(player.p);
    return;
  }
  removeMarble(world, player.p);
  joinState.set(player.p, 'open');
  tournamentTotals.delete(player.p);
});

// Per-race finish order. Position 0 = 1st place, 1 = 2nd, etc. Cleared
// when a fresh race starts (all prevFinished flags reset to false).
const finishOrder: number[] = [];
const FINISH_AWARDS = [5, 3, 2, 1];   // 1st, 2nd, 3rd, 4th

host.onInput((frame) => {
  // Default to slot 0 if the broker dropped p (older broker, single
  // mode). Keeps single-phone play working without multi routing.
  const p = typeof frame.p === 'number' ? frame.p : 0;
  if (gameControls.paused) return;
  initAudio();
  if (spectatorSlots.has(p)) {
    if (frame.k === 'A' && frame.v === '1') {
      cheers.push({ slot: p, until: performance.now() + 1200, colour: PLAYER_COLOURS[p % PLAYER_COLOURS.length] ?? '#b48cff' });
      // Celebratory shake on the host so the audience feels rewarded.
      triggerShake(world, 4, 0.30);
      for (const s of spectatorSlots) host.sendSpectatorView(s, buildSpectatorView());
    }
    return;
  }
  applyInput(world, p, frame.k, frame.v);
});

const gameControls = mountGameControls({
  canStart: () => world.race.kind === 'lobby' && host.players.some(player => player.connected),
  start: () => { initAudio(); tryStartRace(world); },
  restart: () => { Object.assign(world, newWorld()); for (const player of host.players) readyPlayer(player); tryStartRace(world); },
  pause: (paused) => { setAudioPaused(paused); host.broadcast(pauseMessage(paused)); for (const marble of world.marbles.values()) { marble.inThrust = false; marble.inPower = 0; } },
  muted: isMuted,
  toggleSound: () => { initAudio(); toggleMuted(); },
});

window.addEventListener('keydown', (e) => {
  if (e.key === 'm' || e.key === 'M') { initAudio(); toggleMuted(); }
});
canvas.addEventListener('click', () => initAudio());

// World stays in lobby until a human pairs + signals ready. No
// cold-boot AI — the screen reads "scan to play" not "demo mode".
//
// Audio diffs each frame: marble speed drives a continuous rumble,
// hitting a wall fires a thud, dropping into a pit fires a falling
// tone, finishing fires a chord, the boost ramp fires its sweep.
let last = performance.now();
const prevSpeed = new Map<number, number>();
const prevInPit = new Map<number, boolean>();
const prevFinished = new Map<number, boolean>();
const prevBoostUntil = new Map<number, number>();
function loop(t: number): void {
  const dt = Math.min(0.05, (t - last) / 1000);
  last = t;
  if (!gameControls.paused) step(world, dt);
  gameControls.update();
  const muted = isMuted();
  for (const m of world.marbles.values()) {
    const speed = Math.hypot(m.vx, m.vy);
    const intensity = Math.min(1, speed / MAX_SPEED);
    if (!muted) {
      if (speed > 30 && m.finishedAt === null) rumble(m.p, intensity);
      else stopRumble(m.p);
    }
    // Wall thud: previous speed > 200 AND now lost ~60% in a frame
    // (collision response halved velocity). Cheap heuristic for a
    // hit without instrumenting the physics step.
    const ps = prevSpeed.get(m.p) ?? 0;
    if (ps > 220 && speed < ps * 0.5 && m.finishedAt === null) {
      if (!muted) sfxWallThud();
      if (!m.ai) host.haptic('thud', m.p);
    }
    // Pit-fall: position snapped back to the lane start. Detect by
    // checking proximity to any pit on the previous frame transition.
    const inPit = PITS.some((pit) => (m.x - pit.x) ** 2 + (m.y - pit.y) ** 2 < pit.r * pit.r * 0.4);
    const wasInPit = prevInPit.get(m.p) ?? false;
    if (!wasInPit && inPit) {
      if (!muted) sfxPit();
      if (!m.ai) host.haptic('fail', m.p);
    }
    prevInPit.set(m.p, inPit);
    // Boost fired this frame (boostUntil just got set).
    const pb = prevBoostUntil.get(m.p) ?? 0;
    if (m.boostUntil > pb && m.boostUntil > world.time + 0.5) {
      if (!muted) sfxBoost();
      if (!m.ai) host.haptic('tap', m.p);
    }
    // Finish line crossed.
    const pf = prevFinished.get(m.p) ?? false;
    if (!pf && m.finishedAt !== null) {
      if (!muted) sfxFinish();
      const position = finishOrder.length;
      if (!finishOrder.includes(m.p)) finishOrder.push(m.p);
      if (!m.ai) {
        host.haptic('win', m.p);
        const award = FINISH_AWARDS[position] ?? 1;
        const label = finishLabel(position);
        host.awardScore(m.p, award, label);
        tournamentTotals.set(m.p, (tournamentTotals.get(m.p) ?? 0) + award);
      }
    } else if (pf && m.finishedAt === null) {
      // Race reset (finish flag cleared) — drop this slot from the finish order.
      const idx = finishOrder.indexOf(m.p);
      if (idx !== -1) finishOrder.splice(idx, 1);
    }
    prevSpeed.set(m.p, speed);
    prevBoostUntil.set(m.p, m.boostUntil);
    prevFinished.set(m.p, m.finishedAt !== null);
  }
  // Decay cheer bursts.
  const nowMs = performance.now();
  for (let i = cheers.length - 1; i >= 0; i--) {
    if (cheers[i]!.until <= nowMs) cheers.splice(i, 1);
  }
  // Periodic spectator-view refresh.
  spectatorPushTimer -= dt;
  if (spectatorSlots.size > 0 && spectatorPushTimer <= 0) {
    spectatorPushTimer = 1.5;
    for (const s of spectatorSlots) host.sendSpectatorView(s, buildSpectatorView());
  }
  const frame: RenderFrame = { world, pairingUrl: host.pairingUrl, qrDataUrl, sessionId: host.sessionId, joinState, muted, tournamentTotals, playerNames, cheers };
  draw(ctx!, frame);
  requestAnimationFrame(loop);
}
let spectatorPushTimer = 0;
requestAnimationFrame(loop);
